import React, { useContext, useEffect, useReducer, useRef } from 'react';
import { produce } from 'immer';
import { Tabs, Tab } from 'react-bootstrap';
import _ from 'lodash';

import './tracking.component.css';
import AppContext from '../../../context';
import ActivePos from './active_pos/active_pos.component';
import FamilyActivePos from '../../family_pf/active_pos/active_pos.component';
import AllPos from './all_pos/all_pos.component';
import { RadioGroup, Loader } from '../../common/common';
import {
  fetchCompanySummary,
  fetchFamilySummary,
  cancelPrevFetch,
} from '../../../../api';
import ReturnsToggler from './returns_toggler/returns_toggler.component';

const reducer = produce((state, newState) => {
  const { field, value } = newState;
  switch (field) {
    case 'portfolioId':
    case 'currentTab':
    case 'returnsType':
    case 'isMob':
      state[field] = value;
      break;

    case 'data':
    case 'error':
    case 'isLoaderVisible':
    case 'mode':
      state[state.portfolioId][field] = value;
      break;

    case 'resetPfData':
      //pfId as value
      const componentState = {
        data: null,
        isLoaderVisible: true,
        error: '',
      };
      state[value.resetId] = { ...componentState };
      state.family = { ...componentState, mode: 'by_portfolio' };
      state.portfolioId = value.switchId || value.resetId;
      break;

    case 'show':
    case 'currentCompany':
      state[state.currentTab].viewTx[field] = value;
      break;

    case 'isModalVisible':
      state[state.currentTab].deleteTx[field] = value;
      break;

    case 'threedotsContainer':
    case 'showPopover':
      state[state.currentTab][field] = value;
      break;

    default:
      break;
  }
});

const Tracking = (props) => {
  const context = useContext(AppContext);

  //--sample state--
  // {
  //   portfolioId: '123',
  //   currentTab: 'active_positions',
  //   123: {
  //       data: null,
  //       isLoaderVisible: true,
  //       error: '',
  //   },
  //   active_positions: {
  //     viewTx: {
  //       show: false,
  //       currentCompany: null,
  //   },
  //   deleteTx: {
  //       isModalVisible: false
  //   },
  //   threedotsContainer: null,
  //   showPopover: false,
  //   },
  //   returnsType: 'abs',
  //   isMob: false
  // }
  //

  const [state, dispatch] = useReducer(reducer, {}, () => {
    const componentState = {
      data: null,
      isLoaderVisible: true,
      error: '',
    };

    const initialState = {};
    context.portfolios.forEach((pf) => {
      if (pf.id === 'family') {
        return (initialState[pf.id] = {
          ...componentState,
          mode: 'by_portfolio',
        });
      }
      return (initialState[pf.id] = { ...componentState });
    });
    //default portfolio - family or first
    initialState.portfolioId =
      context.portfolios.filter(
        (pf) => !pf.is_dummy && !pf.is_empty && pf.id !== 'family',
      ).length > 1
        ? context.portfolios[0].id
        : context.portfolios[1].id;
    initialState.currentTab = 'active_positions';
    initialState.returnsType = 'abs';
    initialState.isMob = false;
    const tabState = {
      viewTx: {
        show: false,
        currentCompany: null,
      },
      deleteTx: {
        isModalVisible: false,
      },
      threedotsContainer: null,
      showPopover: false,
    };
    initialState.active_positions = _.cloneDeep(tabState);
    initialState.all_positions = _.cloneDeep(tabState);
    return initialState;
  });

  const getCompanySummary = async () => {
    try {
      const res = await fetchCompanySummary(state.portfolioId);
      dispatch({ field: 'data', value: res.data.data.company_summary });
    } catch (error) {
      if (error.message === 'canceled') return;
      dispatch({ field: 'error', value: { text: 'Something went wrong' } });
    }
    if (state[state.portfolioId].isLoaderVisible)
      dispatch({ field: 'isLoaderVisible', value: false });
  };

  const getFamilySummary = async () => {
    try {
      const res = await fetchFamilySummary(true);
      dispatch({ field: 'data', value: res.data.data.summary });
    } catch (error) {
      if (error.message === 'canceled') return;
      dispatch({ field: 'error', value: { text: 'Something went wrong' } });
    }
    if (state[state.portfolioId].isLoaderVisible)
      dispatch({ field: 'isLoaderVisible', value: false });
  };

  const fetchData = () => {
    if (!state[state.portfolioId].data) {
      if (state.portfolioId === 'family') {
        getFamilySummary();
        return;
      }
      cancelPrevFetch('fetchCompanySummary');
      getCompanySummary();
    }
  };

  useEffect(() => {
    fetchData();
  }, [state.portfolioId, state[state.portfolioId].data]);

  useEffect(() => {
    if (context.resetPf.resetId)
      dispatch({ field: 'resetPfData', value: context.resetPf });
  }, [context.resetPf]);

  return (
    <>
      <div className="content_header">
        <h6 className="content-head">Gain & Loss</h6>
        <RadioGroup
          name="tracking"
          checked={state.portfolioId}
          setChecked={(pfId) => dispatch({ field: 'portfolioId', value: pfId })}
        />
      </div>
      {state[state.portfolioId].isLoaderVisible ? (
        <Loader />
      ) : state.portfolioId === 'family' ? (
        <FamilyActivePos
          state={state}
          setState={(field, value) => dispatch({ field, value })}
        />
      ) : (
        <div className="position-relative">
          <div
            className={`d-flex justify-content-between position-relative pos-togglers`}
          >
            <div className="pos-mode cont">
              <button
                type="button"
                className={`item  ${
                  state.currentTab === 'active_positions' ? 'active' : ''
                }`}
                onClick={() =>
                  dispatch({ field: 'currentTab', value: 'active_positions' })
                }
              >
                Active Positions
              </button>
              <button
                type="button"
                className={`item  ${
                  state.currentTab === 'all_positions' ? 'active' : ''
                }`}
                onClick={() =>
                  dispatch({ field: 'currentTab', value: 'all_positions' })
                }
              >
                All Positions
              </button>
            </div>
            <ReturnsToggler
              returnsType={state.returnsType}
              setReturnsType={(t) =>
                dispatch({ field: 'returnsType', value: t })
              }
            />
          </div>
          <>
            <ActivePos
              state={state}
              setState={(field, value) => dispatch({ field, value })}
              setContext={props.setContext}
              show={state.currentTab === 'active_positions'}
            />
            <AllPos
              state={state}
              setState={(field, value) => dispatch({ field, value })}
              setContext={props.setContext}
              show={state.currentTab === 'all_positions'}
            />
          </>
        </div>
      )}
    </>
  );
};

export default Tracking;
